import { Box, Card, CardContent, CardHeader, CardMedia, IconButton, Typography, useTheme } from '@mui/material';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router';
import { useGateApi } from './Api/useGateApi';
import { AuthGateApiClient, AuthResponse, ChangeUserTenantRequest, PortalToolDto, StorePluginSessionTokensRequest, ToolGateApiClient, UsersGateApiClient } from './Api/ApiServerHooks';
import { useGateMainContext } from './GateMainContext';
import { MoreVert } from '@mui/icons-material';
import UpperMenu from './UpperMenu';
import ChooseTenantDialog from './Login/ChooseTenantDialog';
import { useTranslation } from 'react-i18next';

export const SIGMA_BADGE_FONT_FAMILY = '"Cambria Math", "STIX Two Math", "Segoe UI Symbol", serif';

export interface IPortalProps {
}

function renderToolTitle(title?: string) {
    if (!title?.includes('Σ')) return title ?? '';

    const parts = title.split('Σ');

    return parts.reduce<React.ReactNode[]>((nodes, part, index) => {
        if (part) {
            nodes.push(<React.Fragment key={`title-part-${index}`}>{part}</React.Fragment>);
        }

        if (index < parts.length - 1) {
            nodes.push(
                <Box
                    component="span"
                    key={`sigma-${index}`}
                    sx={{
                        fontFamily: SIGMA_BADGE_FONT_FAMILY,
                        fontWeight: 'inherit',
                        fontSize: '1.08em',
                    }}
                >
                    Σ
                </Box>
            );
        }

        return nodes;
    }, []);
}

export default function Portal(props: IPortalProps) {
    const params = useParams();
    const theme = useTheme();
    const { authorizedApi } = useGateApi(AuthGateApiClient);
    const toolApi = useGateApi(ToolGateApiClient);
    const { token, refreshToken, expires, userData, login } = useGateMainContext();
    const [tools, setTools] = React.useState<PortalToolDto[]>([]);
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const refreshTools = React.useCallback(async () => {

        const tools = await toolApi.authorizedApi?.getPortalTools();
        setTools(tools?.tools ?? []);

    }, [toolApi.authorizedApi]);
    React.useEffect(() => {
        if (params.lang){

            i18n.changeLanguage(params.lang);
            navigate('/portal');
        }
    }, [params, i18n, navigate]);
    React.useEffect(() => {
        refreshTools();
    }, [refreshTools, i18n.language]);
    const toolClick = (tool: PortalToolDto) => {
        if (tool.includeLoginInfos) {
            authorizedApi?.storePluginLoginSessionTokens(new StorePluginSessionTokensRequest({
                accessToken: token ?? undefined,
                refreshToken: refreshToken ?? undefined,
                expires: expires ?? undefined
            })).then((session) => {
                let address = tool.url ?? '';
                address += (i18n.language + "/");
                address += session.sessionId;
                window.location.href = address;
            })
        }
        else window.location.href = tool.url ?? '';
    }
    const usersApi = useGateApi(UsersGateApiClient);
    const changeTenant = React.useCallback((r: number) => {
        usersApi.authorizedApi?.changeUserTenant(new ChangeUserTenantRequest({
            tenantId: r
        })).then((res) => {
            if (res) {
                if (res.token === undefined) throw new Error('token is undefined');
                localStorage.setItem('token', res.token);
                localStorage.setItem('token_expires', res.tokenExpiration?.toString()!);
                localStorage.setItem('refresh_token', res.refreshToken!);
                login(new AuthResponse(res));
                // refreshUserData();
                // return true;
                setTenantDialogOpen(false);
            }
        });
    }, [usersApi.authorizedApi, login])
    const [tenantDialogOpen, setTenantDialogOpen] = React.useState(false);
    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
            <ChooseTenantDialog
                selectedTenantId={userData?.tenantId}
                isOpen={tenantDialogOpen}
                onClose={(r) => { if (r) changeTenant(r); else setTenantDialogOpen(false); }} />
            <UpperMenu showChangeTenantDialog={() => setTenantDialogOpen(true)} />
            <Box
                sx={{
                    pt: '80px',
                    px: { xs: 2, sm: 4 },
                    pb: { xs: 3, sm: 4 },
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(320px, 360px))' },
                    gap: 3,
                    justifyContent: { xs: 'center', sm: 'start' },
                }}
            >
                {tools.map((tool) => (
                    <Card
                        raised
                        elevation={5}
                        onClick={() => toolClick(tool)} key={tool.id} sx={{
                            height: 340,
                            display: 'flex',
                            flexDirection: 'column',
                            bgcolor: 'background.paper',
                            color: 'text.primary',
                            overflow: 'hidden',
                            transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                            '&:hover': {
                                cursor: 'pointer',
                                transform: 'translateY(-2px)',
                                boxShadow: 8,
                            }
                        }}>
                        <CardHeader
                            sx={{ height: 56, flexShrink: 0, overflow: 'hidden' }}
                            action={
                                <IconButton onClick={(e) => {
                                    e.stopPropagation();
                                    alert(t('Settings'));
                                }} aria-label={t('Settings')}>
                                    <MoreVert />
                                </IconButton>
                            }
                            title={<Typography fontWeight='bold' color='primary' noWrap>{renderToolTitle(tool.name)}</Typography>}
                        />
                        <CardMedia
                            sx={{ flex: 1, minHeight: 0 }}
                            image={tool.imageUrl}
                            title={tool.name}
                        />
                        <CardContent sx={{
                            flexShrink: 0,
                            height: 52,
                            boxSizing: 'border-box',
                            bgcolor: 'primary.main',
                            color: theme.palette.getContrastText(theme.palette.primary.main),
                            p: '8px 14px',
                            '&:last-child': { pb: '8px' },
                        }}>
                            <Typography
                                fontSize={12}
                                lineHeight={1.4}
                                sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                }}
                            >
                                {tool.description}
                            </Typography>
                        </CardContent></Card>
                ))
                }


            </Box >
        </Box>
    );
}
